import { AuthExpired, SourceError } from '../../lib/errors.ts';
import type { ComicCard, ComicSource, SourceAuth, SourceId } from '../../sources/types.ts';
import { requestAccountDataAutoSync } from '../account/accountDataAutoSync.ts';

const DEFAULT_SOURCE_FAVORITE_CATEGORY = 0;

export interface FavoriteMutationDeps {
  saveApp: (card: ComicCard) => Promise<void>;
  unsaveApp: (sourceId: SourceId, comicId: string) => Promise<void>;
  markSource: (card: ComicCard, opts?: { sourceNativeCat?: number }) => Promise<void>;
  unmarkSource: (sourceId: SourceId, comicId: string) => Promise<void>;
}

export function canSaveInApp(source: ComicSource): boolean {
  return source.capabilities.favorites;
}

export function supportsSourceFavorites(source: ComicSource): boolean {
  return source.capabilities.favorites && Boolean(source.addFavorite && source.removeFavorite);
}

export function needsSourceFavoriteAuth(source: ComicSource): boolean {
  return source.capabilities.login && Boolean(source.addFavorite && source.removeFavorite);
}

function requireNativeFavoriteAuth(source: ComicSource, auth: SourceAuth | undefined): SourceAuth {
  if (auth && !auth.anonymous) return auth;
  throw new AuthExpired(`Connect ${source.name} to update source-site favorites.`, {
    source: source.id,
  });
}

export async function saveFavorite(
  source: ComicSource,
  comic: ComicCard,
  deps: FavoriteMutationDeps,
): Promise<void> {
  if (!canSaveInApp(source)) {
    throw new SourceError(`${source.name} does not support saving in Bunko.`, {
      source: source.id,
    });
  }

  await deps.saveApp(comic);
  requestAccountDataAutoSync();
}

export async function unsaveFavorite(
  source: ComicSource,
  comicId: string,
  deps: FavoriteMutationDeps,
): Promise<void> {
  if (!canSaveInApp(source)) {
    throw new SourceError(`${source.name} does not support saving in Bunko.`, {
      source: source.id,
    });
  }

  await deps.unsaveApp(source.id, comicId);
  requestAccountDataAutoSync();
}

export async function addSourceFavorite(
  source: ComicSource,
  comic: ComicCard,
  auth: SourceAuth | undefined,
  deps: FavoriteMutationDeps,
): Promise<void> {
  const addNativeFavorite = source.addFavorite;
  if (!supportsSourceFavorites(source) || !addNativeFavorite) {
    throw new SourceError(`${source.name} does not support adding source-site favorites.`, {
      source: source.id,
    });
  }
  const nativeAuth = requireNativeFavoriteAuth(source, auth);
  await addNativeFavorite(comic.id, nativeAuth, DEFAULT_SOURCE_FAVORITE_CATEGORY);
  await deps.markSource(comic, { sourceNativeCat: DEFAULT_SOURCE_FAVORITE_CATEGORY });
  requestAccountDataAutoSync();
}

export async function removeSourceFavorite(
  source: ComicSource,
  comicId: string,
  auth: SourceAuth | undefined,
  deps: FavoriteMutationDeps,
): Promise<void> {
  const removeNativeFavorite = source.removeFavorite;
  if (!supportsSourceFavorites(source) || !removeNativeFavorite) {
    throw new SourceError(`${source.name} does not support removing source-site favorites.`, {
      source: source.id,
    });
  }
  const nativeAuth = requireNativeFavoriteAuth(source, auth);
  await removeNativeFavorite(comicId, nativeAuth);
  await deps.unmarkSource(source.id, comicId);
  requestAccountDataAutoSync();
}
